import { SpatialHash } from "@eldrun/shared";
import { sampleGradient, sampleHeight, SEA_LEVEL, type Heightfield } from "@eldrun/worldgen";
import type { Box } from "./building.ts";
import type { Cylinder, SimWorld } from "./movement.ts";
import { MOVE } from "./movement-constants.ts";

/** Caixa até esta espessura é piso (o convés tem 1,6 m; o piso da construção, 0,2 m). */
const FLOOR_THICKNESS = 1.6;
/** Lado da célula da grade das caixas (m). */
const BOX_CELL = 8;
const boxCell = (v: number) => Math.floor(v / BOX_CELL);
/** Chave numérica da célula (o mundo vai de −1024 a 1024 m: 128 células para cada lado). */
const cellKey = (i: number, j: number) => (i + 4096) * 8192 + (j + 4096);
const NO_IDS: readonly number[] = [];
/**
 * Ordena `a[0…n)` em ordem crescente, no lugar (inserção: as listas são curtas, e o `sort` do motor
 * aloca um buffer de trabalho a cada chamada; as buscas rodam a cada passo e a cada quadro).
 */
function sortPrefix(a: number[], n: number): void {
  for (let i = 1; i < n; i++) {
    const v = a[i] as number;
    let j = i - 1;
    while (j >= 0 && (a[j] as number) > v) {
      a[j + 1] = a[j] as number;
      j--;
    }
    a[j + 1] = v;
  }
}

/**
 * Mundo da simulação sobre o heightfield (idêntico no cliente e no servidor) mais os cilindros
 * estáticos indexados numa grade espacial. Os recursos entram aqui na F08.
 */
export class HeightfieldWorld implements SimWorld {
  readonly seaLevel = SEA_LEVEL;
  private readonly hf: Heightfield;
  private readonly grid = new SpatialHash<number>(8);
  private readonly colliders = new Map<number, Cylinder>();
  private readonly ids: number[] = [];
  private readonly cylinderOut: Cylinder[] = [];
  private readonly boxOut: Box[] = [];
  /**
   * Caixas (peças de construção da F17, paredes da ruína, faixas da laje da Caverna) numa grade de 8 m,
   * cada uma em toda célula que ela toca: a busca só olha as células do quadrado pedido e só devolve as
   * caixas que o tocam (as dezenas de faixas da laje não pesam em cada passo perto dela).
   */
  private readonly boxCells = new Map<number, number[]>();
  private readonly boxKeys = new Map<number, number[]>();
  private readonly boxMap = new Map<number, Box>();
  private readonly boxIds: number[] = [];
  /** Maior raio de cilindro já registrado: a busca dos cilindros (pelo centro) o cobre. */
  private maxRadius = 2;
  private readonly floorScratch: Box[] = [];

  constructor(heightfield: Heightfield) {
    this.hf = heightfield;
  }

  height(x: number, z: number): number {
    return sampleHeight(this.hf, x, z);
  }

  /**
   * Chão onde algo cai em (x, z): o terreno ou, por cima dele, um piso (caixa baixa: o convés da
   * Jangada, o piso de uma casa). Paredes (caixas altas) não contam, nem tetos suspensos (a laje da
   * Caverna: quem está embaixo continua no chão), a não ser que `standing` (a altura de quem está ali,
   * o alvo de um golpe mirado) diga que se pisa nele. Igual no cliente e no servidor.
   */
  floorHeight(x: number, z: number, standing?: number): number {
    let y = this.height(x, z);
    this.floorScratch.length = 0;
    for (const b of this.boxes(x, z, 0, this.floorScratch)) {
      if (b.ceiling && (standing === undefined || b.maxY > standing + MOVE.stepHeight)) continue;
      if (x < b.minX || x > b.maxX || z < b.minZ || z > b.maxZ || b.maxY - b.minY > FLOOR_THICKNESS) continue;
      if (b.maxY > y) y = b.maxY;
    }
    return y;
  }

  gradient(x: number, z: number, out: { x: number; z: number }): { x: number; z: number } {
    return sampleGradient(this.hf, x, z, out);
  }

  addCylinder(id: number, cylinder: Cylinder): void {
    if (cylinder.radius > this.maxRadius) this.maxRadius = cylinder.radius;
    this.colliders.set(id, cylinder);
    this.grid.set(id, cylinder.x, cylinder.z);
  }

  removeCylinder(id: number): void {
    this.colliders.delete(id);
    this.grid.delete(id);
  }

  addBox(id: number, box: Box): void {
    if (this.boxMap.has(id)) this.removeBox(id);
    this.boxMap.set(id, box);
    const keys: number[] = [];
    for (let i = boxCell(box.minX); i <= boxCell(box.maxX); i++) {
      for (let j = boxCell(box.minZ); j <= boxCell(box.maxZ); j++) {
        const key = cellKey(i, j);
        keys.push(key);
        const list = this.boxCells.get(key);
        if (list) list.push(id);
        else this.boxCells.set(key, [id]);
      }
    }
    this.boxKeys.set(id, keys);
  }

  removeBox(id: number): void {
    for (const key of this.boxKeys.get(id) ?? NO_IDS) {
      const list = this.boxCells.get(key) as number[];
      list.splice(list.indexOf(id), 1);
      if (list.length === 0) this.boxCells.delete(key);
    }
    this.boxKeys.delete(id);
    this.boxMap.delete(id);
  }

  /** Caixas que tocam o quadrado de meio lado `radius` em volta de (x, z), em ordem estável (por id), em `out`. */
  boxes(x: number, z: number, radius: number, out: Box[]): Box[] {
    const n = this.boxesInto(x, z, radius, this.boxOut);
    for (let i = 0; i < n; i++) out.push(this.boxOut[i] as Box);
    return out;
  }

  /**
   * Como `boxes`, escrevendo em `out[0…]` por índice e devolvendo quantas: `out` nunca encolhe, e nada
   * é alocado (a câmera e a mira buscam várias vezes por quadro, F55).
   */
  boxesInto(x: number, z: number, radius: number, out: Box[]): number {
    const x0 = x - radius;
    const x1 = x + radius;
    const z0 = z - radius;
    const z1 = z + radius;
    let n = 0;
    for (let i = boxCell(x0); i <= boxCell(x1); i++) {
      for (let j = boxCell(z0); j <= boxCell(z1); j++) {
        const list = this.boxCells.get(cellKey(i, j));
        if (!list) continue;
        for (let k = 0; k < list.length; k++) {
          const id = list[k] as number;
          const b = this.boxMap.get(id) as Box;
          if (b.maxX >= x0 && b.minX <= x1 && b.maxZ >= z0 && b.minZ <= z1) this.boxIds[n++] = id;
        }
      }
    }
    // Ordem estável (por id), sem repetir a caixa que está em mais de uma célula.
    sortPrefix(this.boxIds, n);
    let m = 0;
    let last = Number.NaN;
    for (let i = 0; i < n; i++) {
      const id = this.boxIds[i] as number;
      if (id !== last) out[m++] = this.boxMap.get(id) as Box;
      last = id;
    }
    return m;
  }

  cylinders(x: number, z: number, radius: number, out: Cylinder[]): Cylinder[] {
    const n = this.cylindersInto(x, z, radius, this.cylinderOut);
    for (let i = 0; i < n; i++) out.push(this.cylinderOut[i] as Cylinder);
    return out;
  }

  /** Como `cylinders`, escrevendo em `out[0…]` por índice e devolvendo quantos (sem alocar). */
  cylindersInto(x: number, z: number, radius: number, out: Cylinder[]): number {
    // Indexados pelo centro: o raio de busca inclui o maior raio registrado (o fundo da gruta tem 2,6 m).
    const n = this.grid.queryRadiusInto(x, z, radius + this.maxRadius, this.ids);
    // Ordem estável (por id) para a resolução ser idêntica nos dois lados.
    sortPrefix(this.ids, n);
    for (let i = 0; i < n; i++) out[i] = this.colliders.get(this.ids[i] as number) as Cylinder;
    return n;
  }
}
